<template>
  <v-app-bar app color="primary" density="compact" class="navigation-bar">
    <!-- Mobile: Hamburger Menu -->
    <v-app-bar-nav-icon class="d-md-none" @click="drawer = !drawer" aria-label="Menu" />

    <!-- Logo -->
    <div class="header-logo" @click="backToSystem" :class="{ 'cursor-pointer': hasBacklink() }">
      <img src="/logo.png" alt="Live Nation GmbH - Berlin (Chausseestraße)" />
    </div>

    <!-- Desktop Navigation -->
    <div class="d-none d-md-flex navigation-items">
      <template v-for="item in navigationStore.items" :key="item.key">
        <!-- Item with children (dropdown) -->
        <v-menu v-if="item.children && item.children.length > 0" offset-y>
          <template #activator="{ props }">
            <v-btn v-bind="props" variant="text" class="nav-btn" :ripple="false">
              <v-icon v-if="item.icon" :icon="convertIcon(item.icon)" class="mr-1" size="small" />
              <span v-else>{{ item.label }}</span>
            </v-btn>
          </template>
          <v-list density="compact" class="nav-dropdown">
            <v-list-item
              v-for="child in item.children"
              :key="child.key"
              :href="getFullUrl(child.url)"
              :target="child.target || '_self'"
              class="nav-dropdown-item"
            >
              <v-list-item-title>{{ child.label }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>

        <!-- Simple item (no children) -->
        <v-btn
          v-else
          variant="text"
          :href="getFullUrl(item.url)"
          :target="item.target || '_self'"
          class="nav-btn"
          :ripple="false"
        >
          <v-icon v-if="item.icon" :icon="convertIcon(item.icon)" size="small" />
          <span v-else>{{ item.label }}</span>
        </v-btn>
      </template>
    </div>

    <v-spacer />
  </v-app-bar>

  <!-- Mobile Navigation Drawer -->
  <v-navigation-drawer v-model="drawer" temporary location="left" class="mobile-drawer">
    <v-list density="compact" nav class="mobile-nav-list">
      <template v-for="item in navigationStore.items" :key="item.key">
        <!-- Item with children -->
        <v-list-group v-if="item.children && item.children.length > 0" :value="item.key" class="mobile-nav-group">
          <template #activator="{ props }">
            <v-list-item v-bind="props" class="mobile-nav-parent" :ripple="false">
              <template #prepend>
                <v-icon v-if="item.icon" :icon="convertIcon(item.icon)" size="small" class="mobile-nav-icon" />
              </template>
              <v-list-item-title class="mobile-nav-title">{{ item.label }}</v-list-item-title>
            </v-list-item>
          </template>

          <v-list-item
            v-for="child in item.children"
            :key="child.key"
            :href="getFullUrl(child.url)"
            :target="child.target || '_self'"
            class="mobile-nav-child"
            @click="drawer = false"
          >
            <v-list-item-title class="mobile-nav-child-title">{{ child.label }}</v-list-item-title>
          </v-list-item>
        </v-list-group>

        <!-- Simple item -->
        <v-list-item
          v-else
          :href="getFullUrl(item.url)"
          :target="item.target || '_self'"
          class="mobile-nav-parent"
          :ripple="false"
          @click="drawer = false"
        >
          <template #prepend>
            <v-icon v-if="item.icon" :icon="convertIcon(item.icon)" size="small" class="mobile-nav-icon" />
          </template>
          <v-list-item-title class="mobile-nav-title">{{ item.label }}</v-list-item-title>
        </v-list-item>
      </template>
    </v-list>
  </v-navigation-drawer>
</template>

<script lang="ts" setup>
import { ref, onMounted, onUnmounted } from "vue"
import { useRoute } from "vue-router"
import { useNavigationStore } from "@/stores/navigationStore"

const route = useRoute()
const navigationStore = useNavigationStore()
const drawer = ref(false)
const id = ref<number | undefined>()

/**
 * Check if token exists in sessionStorage
 */
const hasToken = (): boolean => {
  return sessionStorage.getItem("token") !== null
}

/**
 * Load navigation with retry logic.
 * If no token is available yet, wait and retry.
 */
const loadNavigationWithRetry = async (retryCount = 0): Promise<void> => {
  // If we have items already, don't reload
  if (navigationStore.hasItems) {
    return
  }

  // If no token yet and we haven't exceeded retries, wait and retry
  if (!hasToken() && retryCount < 10) {
    setTimeout(() => {
      loadNavigationWithRetry(retryCount + 1)
    }, 300) // Wait 300ms between retries
    return
  }

  // Try to load navigation
  await navigationStore.loadNavigation(true) // force reload
}

onMounted(async () => {
  // Initial load attempt
  await loadNavigationWithRetry()

  // Extract ID from route if needed for backlink
  if (route && route.name === "venue-specs" && route.params.id) {
    id.value = parseInt(route.params.id.toString())
  }
  if (route && route.name === "requirements" && route.params.id) {
    id.value = parseInt(route.params.id.toString())
  }
})

/**
 * Handle storage changes (token being set)
 */
const handleStorageChange = (event: StorageEvent): void => {
  if (event.key === "token" && event.newValue && !navigationStore.hasItems) {
    navigationStore.loadNavigation(true)
  }
}

// Add storage listener on mount, remove on unmount
onMounted(() => {
  if (typeof window !== "undefined") {
    window.addEventListener("storage", handleStorageChange)
  }
})

onUnmounted(() => {
  if (typeof window !== "undefined") {
    window.removeEventListener("storage", handleStorageChange)
  }
})

/**
 * Convert FontAwesome class to MDI icon format
 * Falls back to simple icon name if conversion not possible
 */
const convertIcon = (iconClass: string | undefined): string => {
  if (!iconClass) {
    return ""
  }

  // Map common FontAwesome icons to MDI equivalents
  const iconMap: Record<string, string> = {
    "fa-solid fa-house": "mdi-home",
    "fa-solid fa-gear": "mdi-cog"
  }

  return iconMap[iconClass] || "mdi-circle-small"
}

/**
 * Build full URL for navigation links
 */
const getFullUrl = (path: string | undefined): string => {
  if (!path) {
    return "#"
  }
  // @ts-expect-error - Vite injects import.meta.env at build time
  return import.meta.env.VITE_CONCERTON_URL + path
}

/**
 * Handle logo click - navigate back to appropriate page
 */
const backToSystem = (): void => {
  if (!hasBacklink()) {
    return
  }

  // @ts-expect-error - Vite injects import.meta.env at build time
  const baseUrl = import.meta.env.VITE_CONCERTON_URL

  // Special cases with specific redirects
  if (route && route.name === "incoming-invoice") {
    window.location.href = baseUrl + "/incoming-invoice/index"
  } else if (route && route.name === "venue-specs") {
    window.location.href = baseUrl + "/company/details?id=" + id.value + "&tab=venue-specs"
  } else if (route && route.name === "requirements") {
    window.location.href = baseUrl + "/tour/details?id=" + id.value + "&tab=requirements"
  } else {
    // Default: link back to main index page
    window.location.href = baseUrl
  }
}

/**
 * Check if logo should have a backlink
 */
const hasBacklink = (): boolean => {
  if (route && route.name === "tour-cash-register") {
    return false
  }
  return true
}
</script>

<style scoped>
.navigation-bar {
  background-color: #1e262b !important;
}

.header-logo {
  padding: 5px 10px;
  display: flex;
  align-items: center;
}

.header-logo img {
  height: 32px;
}

.cursor-pointer {
  cursor: pointer;
}

.navigation-items {
  display: flex;
  align-items: center;
  margin-left: 10px;
}

.nav-btn {
  color: white !important;
  text-transform: none !important;
  font-size: 0.875rem !important;
  min-width: auto !important;
  padding: 0 8px !important;
  text-decoration: none !important;
}

.nav-btn:hover {
  text-decoration: none !important;
}

/* Disable Vuetify hover overlay on main nav buttons */
.nav-btn :deep(.v-btn__overlay) {
  display: none !important;
}

.nav-dropdown {
  max-height: 400px;
  overflow-y: auto;
}

.nav-dropdown-item {
  min-height: 36px !important;
}

.nav-dropdown-item:hover {
  background-color: rgba(0, 0, 0, 0.08) !important;
}

/* Remove underlines from all navigation links */
.nav-dropdown-item :deep(a),
.nav-dropdown :deep(a),
.navigation-items :deep(a) {
  text-decoration: none !important;
}

.mobile-drawer {
  background-color: #1e262b !important;
}

/* Mobile navigation list */
.mobile-nav-list {
  padding: 8px 0 !important;
  background-color: transparent !important;
}

/* Parent items (top-level) */
.mobile-nav-parent {
  color: white !important;
  border-radius: 0 !important;
  margin: 0 !important;
  padding: 12px 16px !important;
  min-height: 44px !important;
}

/* Disable hover on mobile parent items */
.mobile-nav-parent :deep(.v-list-item__overlay) {
  display: none !important;
}

.mobile-nav-group :deep(.v-list-group__header .v-list-item__overlay) {
  display: none !important;
}

.mobile-nav-title {
  color: white !important;
  font-weight: 500 !important;
  font-size: 0.95rem !important;
}

.mobile-nav-icon {
  color: white !important;
  margin-right: 12px !important;
}

/* Child items (sub-menu) - Light background like desktop dropdown */
.mobile-nav-child {
  background-color: #ffffff !important;
  padding: 10px 16px 10px 32px !important;
  min-height: 40px !important;
  margin: 0 !important;
  border-radius: 0 !important;
  margin-left: 0 !important;
  padding-inline-start: 32px !important;
}

.mobile-nav-child:hover {
  background-color: rgba(0, 0, 0, 0.08) !important;
}

.mobile-nav-child-title {
  color: rgba(0, 0, 0, 0.87) !important;
  font-size: 0.875rem !important;
  font-weight: 400 !important;
}

/* Group styling */
.mobile-nav-group :deep(.v-list-group__header) {
  color: white !important;
  min-height: 44px !important;
}

/* Light background for child items container */
.mobile-nav-group :deep(.v-list-group__items) {
  background-color: #ffffff !important;
  --indent-padding: 0px !important;
}

/* Expand icon */
.mobile-drawer :deep(.v-list-group__header .v-icon) {
  color: rgba(255, 255, 255, 0.7) !important;
}

/* Remove underlines from all v-list-item links */
:deep(.v-list-item) {
  text-decoration: none !important;
}

:deep(.v-list-item:hover) {
  text-decoration: none !important;
}

/* Ensure buttons don't have underlines */
:deep(.v-btn) {
  text-decoration: none !important;
}
</style>
